/**
 * Which first-time hints the player has seen, and whether they want hints at all (docs/ux.md
 * U-LEARN-08). Kept in the captain's profile so it follows them across devices; without a
 * profile (tests, a page outside the API) the browser keeps it.
 */
import { HINT_KEYS, type HintKey } from '@brig/api-types';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useMe, useUpdateMe } from '../api/hooks.ts';

export interface HintMemory {
  readonly on: boolean;
  readonly seen: ReadonlySet<string>;
  readonly markSeen: (key: HintKey) => void;
  readonly setOn: (on: boolean) => void;
  /** "Show all hints again". */
  readonly reset: () => void;
}

/** The profile's memory, provided around a game screen that has one. */
export const HintMemoryContext = createContext<HintMemory | null>(null);

const KEY = 'brig.hints.v1';

const isHintKey = (key: string): key is HintKey => (HINT_KEYS as readonly string[]).includes(key);

interface Stored {
  readonly on: boolean;
  readonly seen: readonly string[];
}

function load(): Stored {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Stored>;
    return { on: saved.on ?? true, seen: Array.isArray(saved.seen) ? saved.seen : [] };
  } catch {
    return { on: true, seen: [] };
  }
}

/** The browser's own memory: the fallback when there is no profile. */
export function useLocalHintMemory(): HintMemory {
  const [stored, setStored] = useState(load);
  const save = useCallback((next: Stored) => {
    setStored(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Without storage the hints last for the session; that is all.
    }
  }, []);
  return useMemo(
    () => ({
      on: stored.on,
      seen: new Set(stored.seen),
      markSeen: (key) => {
        if (!stored.seen.includes(key)) save({ ...stored, seen: [...stored.seen, key] });
      },
      setOn: (on) => {
        save({ ...stored, on });
      },
      reset: () => {
        save({ ...stored, seen: [] });
      },
    }),
    [stored, save],
  );
}

/**
 * The profile's memory, or null until the profile has loaded. A hint dismissed here is gone at
 * once, before the server answers.
 */
export function useProfileHintMemory(): HintMemory | null {
  const me = useMe().data;
  const { mutate } = useUpdateMe();
  const [dismissed, setDismissed] = useState<readonly HintKey[]>([]);
  return useMemo(() => {
    if (!me) return null;
    const seen = new Set<string>([...me.hintsSeen, ...dismissed]);
    return {
      on: me.hintsOn,
      seen,
      markSeen: (key) => {
        if (seen.has(key)) return;
        setDismissed((list) => [...list, key]);
        // Unknown keys a later version dropped fall away here: the server takes known ones only.
        mutate({ hintsSeen: [...seen, key].filter(isHintKey) });
      },
      setOn: (on) => {
        mutate({ hintsOn: on });
      },
      reset: () => {
        setDismissed([]);
        mutate({ hintsSeen: [] });
      },
    };
  }, [me, dismissed, mutate]);
}
